/**
 * Espace d'administration — port du shell Lovable (routes/admin.tsx), branché
 * sur l'authentification réelle.
 *
 * Deux sections de navigation : Organisateur (tout compte connecté) et
 * Plateforme (drapeau super-admin réel — pas de sélecteur de rôle fictif :
 * on voit ce que ses droits donnent). Les écrans dont le backend n'existe pas
 * encore affichent une page « en construction » fidèle à la maquette, jamais
 * de fausses données présentées comme réelles.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X } from 'lucide-react';
import { appelerApi, envoyer } from '../reseau.ts';
import { useLibelles } from '../i18n.tsx';
import { Marque } from '../composants/Marque.tsx';

import { TableauDeBord } from './pages/TableauDeBord.tsx';
import { Evenements } from './pages/Evenements.tsx';
import { Billetterie } from './pages/Billetterie.tsx';
import { Pistes } from './pages/Pistes.tsx';
import { Automatisations } from './pages/Automatisations.tsx';
import { Integrations } from './pages/Integrations.tsx';
import { Plateforme } from './pages/Plateforme.tsx';
import { Commissions } from './pages/Commissions.tsx';
import { Organisations } from './pages/Organisations.tsx';
import { EnConstruction } from './pages/EnConstruction.tsx';

export type CompteAdmin = {
  nom_affiche: string;
  email: string;
  role: string;
  est_super_admin?: boolean;
  /** Mot de passe posé par la plateforme : à redéfinir avant d'entrer. */
  doit_redefinir?: boolean;
};

export type EvenementAdmin = {
  id: string;
  nom: string;
  slug: string;
  lieu_nom: string | null;
  lieu_ville: string | null;
  debut_le: string;
  fin_le: string;
  statut: string;
  /** Inscription libre depuis un compte personnel (migration 0008). */
  visibilite_publique?: boolean;
  categorie: 'networking' | 'sport' | 'formation';
  sous_categorie: 'jouer' | 'regarder' | null;
};

type Session = { compte: CompteAdmin; organisation: { slug: string }; evenements: EvenementAdmin[] };

const NAV_ORGA = [
  { to: '/orga', label: 'Tableau de bord', exact: true },
  { to: '/orga/evenements', label: 'Mes événements' },
  { to: '/orga/billetterie', label: 'Billetterie' },
  { to: '/orga/entreprises', label: 'Entreprises inscrites' },
  { to: '/orga/reseaux', label: 'Réseaux' },
  { to: '/orga/sponsors', label: 'Sponsors' },
  { to: '/orga/revenus', label: 'Revenus & parrainage' },
  { to: '/orga/parrainage', label: 'Mes filleuls' },
  { to: '/orga/rapports', label: 'Rapports' },
  { to: '/orga/equipe', label: 'Mon équipe (Premium)' },
] as const;

const NAV_PLATEFORME = [
  { to: '/orga/plateforme', label: "Vue d'ensemble" },
  { to: '/orga/organisations', label: 'Organisations' },
  { to: '/orga/commerciaux', label: 'Commerciaux & sous-commerciaux' },
  { to: '/orga/comptes', label: 'Entreprises & visiteurs' },
  { to: '/orga/pistes', label: 'Pistes de prospection' },
  { to: '/orga/automatisations', label: 'Automatisations' },
  { to: '/orga/integrations', label: 'Intégrations & paramètres' },
  { to: '/orga/commissions', label: 'Commissions (grand livre)' },
  { to: '/orga/ia', label: 'Consommation IA' },
] as const;

export function EspaceAdmin() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  const charger = async () => {
    const r = await appelerApi<Session>('/api/organisateur/moi');
    setSession(r.donnees ?? null);
  };

  useEffect(() => {
    void charger();
  }, []);

  if (session === undefined) {
    return <p className="min-h-screen bg-void p-10 text-fumee">Chargement…</p>;
  }
  if (session === null) {
    return <Connexion onConnecte={() => void charger()} />;
  }
  if (session.compte.doit_redefinir) {
    return <RedefinirMotDePasse email={session.compte.email} onFait={() => void charger()} />;
  }

  return <Shell session={session} onDeconnecte={() => setSession(null)} />;
}

/* ————————————————— Connexion ————————————————— */

function Connexion({ onConnecte }: { onConnecte: () => void }) {
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEnCours(true);
    setErreur(null);
    const r = await envoyer('/api/organisateur/connexion', { email, motDePasse });
    setEnCours(false);
    if (r.erreur) setErreur(r.erreur);
    else onConnecte();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-4">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5">
          <Marque />
          <span className="text-[17px] font-medium text-blanc">
            EventMatch<span className="text-fumee">.pro</span>
          </span>
        </div>
        <h1 className="display-title mt-8 text-[32px] text-blanc">
          Espace <em className="text-match-gradient font-serif italic font-medium">organisateur</em>
        </h1>
        <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
          <label className="block">
            <span className="label-caps block text-fumee">E-mail</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="field-input mt-2"
            />
          </label>
          <label className="block">
            <span className="label-caps block text-fumee">Mot de passe</span>
            <input
              type="password"
              required
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              autoComplete="current-password"
              className="field-input mt-2"
            />
          </label>
          {erreur && (
            <p className="text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
              {erreur}
            </p>
          )}
          <button type="submit" className="btn-creme mt-2" disabled={enCours}>
            {enCours ? '…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ————————————————— Première connexion : redéfinir le mot de passe ————————————————— */

function RedefinirMotDePasse({ email, onFait }: { email: string; onFait: () => void }) {
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (nouveau.length < 8) return setErreur('Au moins 8 caractères.');
    if (nouveau !== confirmation) return setErreur('Les deux saisies ne correspondent pas.');
    setEnCours(true);
    setErreur(null);
    const r = await envoyer('/api/organisateur/mot-de-passe', { nouveau });
    setEnCours(false);
    if (r.erreur) setErreur(r.erreur);
    else onFait();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-4">
      <div className="w-full max-w-[420px]">
        <div className="flex items-center gap-2.5">
          <Marque />
          <span className="text-[17px] font-medium text-blanc">EventMatch<span className="text-fumee">.pro</span></span>
        </div>
        <h1 className="display-title mt-8 text-[30px] text-blanc">
          Choisissez votre <em className="text-match-gradient font-serif italic font-medium">mot de passe</em>
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-fumee">
          Le mot de passe provisoire de <span className="text-blanc">{email}</span> a été posé par la plateforme :
          définissez le vôtre avant d’entrer. Il ne sera connu que de vous.
        </p>
        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          <label className="block">
            <span className="label-caps block text-fumee">Nouveau mot de passe</span>
            <input type="password" required minLength={8} value={nouveau} onChange={(e) => setNouveau(e.target.value)} autoComplete="new-password" className="field-input mt-2" autoFocus />
          </label>
          <label className="block">
            <span className="label-caps block text-fumee">Confirmez-le</span>
            <input type="password" required minLength={8} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="new-password" className="field-input mt-2" />
          </label>
          {erreur && <p className="text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
          <button type="submit" className="btn-creme mt-2" disabled={enCours}>{enCours ? '…' : 'Enregistrer et entrer'}</button>
        </form>
      </div>
    </div>
  );
}

/* ————————————————— Shell + sidebar ————————————————— */

function LienNav({ to, label, exact }: { to: string; label: string; exact?: boolean }) {
  return (
    <NavLink
      to={to}
      end={Boolean(exact)}
      className="group relative block py-2 pl-4 text-[14px] text-fumee transition-colors duration-200 hover:text-blanc"
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="bg-match-gradient absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full" />
          )}
          <span className={isActive ? 'text-blanc' : ''}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function changerLangue(langue: string) {
  try {
    localStorage.setItem('eventmatch.langue', langue);
  } catch {
    /* stockage indisponible */
  }
  location.reload();
}

function Sidebar({
  session,
  onDeconnecte,
  onNavigate,
}: {
  session: Session;
  onDeconnecte: () => void;
  onNavigate?: () => void;
}) {
  const { langue } = useLibelles();
  const naviguer = useNavigate();
  const superAdmin = Boolean(session.compte.est_super_admin);
  const initiales = session.compte.nom_affiche
    .split(/\s+/)
    .map((m) => m[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-full flex-col justify-between bg-void px-5 py-6" onClick={onNavigate}>
      <div>
        <a href="/" className="flex items-center gap-2.5">
          <Marque taille={20} />
          <span className="text-[15px] font-medium text-blanc">
            EventMatch<span className="text-fumee">.pro</span>
          </span>
        </a>

        <nav className="mt-9">
          <p className="label-caps mb-3 pl-4 text-fumee/70">Organisateur</p>
          <div className="flex flex-col gap-0.5">
            {NAV_ORGA.map((l) => (
              <LienNav key={l.to} {...l} />
            ))}
          </div>

          {superAdmin && (
            <>
              <div className="my-5 border-t border-graphite" />
              <p className="label-caps mb-3 pl-4 text-fumee/70">Plateforme</p>
              <div className="flex flex-col gap-0.5">
                {NAV_PLATEFORME.map((l) => (
                  <LienNav key={l.to} {...l} />
                ))}
              </div>
            </>
          )}
        </nav>
      </div>

      <div className="mt-8 border-t border-graphite pt-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-graphite text-[12px] text-blanc">
            {initiales || '·'}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[14px] text-blanc">{session.compte.nom_affiche}</p>
            <p className="truncate text-[12px] text-fumee">{session.compte.email}</p>
          </div>
        </div>

        <div className="label-caps mt-4 flex items-center gap-2 text-fumee">
          {(['fr', 'nl', 'en'] as const).map((l, i) => (
            <span key={l} className="flex items-center gap-2">
              {i > 0 && <span aria-hidden="true">·</span>}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  changerLangue(l);
                }}
                className={`cursor-pointer uppercase hover:text-blanc ${l === langue ? 'text-blanc' : ''}`}
              >
                {l}
              </button>
            </span>
          ))}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            void envoyer('/api/organisateur/sortir', {}).then(() => {
              onDeconnecte();
              naviguer('/orga');
            });
          }}
          className="mt-4 flex items-center gap-2 text-[13px] text-fumee transition-colors duration-200 hover:text-blanc"
        >
          <LogOut size={14} strokeWidth={1.5} />
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

function Shell({ session, onDeconnecte }: { session: Session; onDeconnecte: () => void }) {
  const [ouvert, setOuvert] = useState(false);
  const superAdmin = Boolean(session.compte.est_super_admin);

  const enConstruction = (titre: string, note: string): ReactNode => (
    <EnConstruction titre={titre} note={note} />
  );

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[248px] overflow-y-auto border-r border-graphite lg:block">
        <Sidebar session={session} onDeconnecte={onDeconnecte} />
      </aside>

      <div className="flex items-center justify-between border-b border-graphite px-4 py-3 lg:hidden">
        <span className="text-[15px] font-medium">Administration</span>
        <button onClick={() => setOuvert(true)} aria-label="Ouvrir la navigation" className="text-fumee hover:text-blanc">
          <Menu size={20} strokeWidth={1.5} />
        </button>
      </div>

      {ouvert && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-void/80" onClick={() => setOuvert(false)} />
          <div className="absolute inset-y-0 left-0 w-[272px] overflow-y-auto border-r border-graphite bg-void">
            <button
              onClick={() => setOuvert(false)}
              aria-label="Fermer la navigation"
              className="absolute right-3 top-4 z-10 text-fumee hover:text-blanc"
            >
              <X size={18} strokeWidth={1.5} />
            </button>
            <Sidebar session={session} onDeconnecte={onDeconnecte} onNavigate={() => setOuvert(false)} />
          </div>
        </div>
      )}

      <main className="px-4 pb-24 pt-8 sm:px-8 lg:ml-[248px] lg:px-10 lg:py-12">
        <div className="mx-auto max-w-[1180px]">
          <Routes>
            <Route path="/orga" element={<TableauDeBord evenements={session.evenements} />} />
            <Route path="/orga/evenements" element={<Evenements evenements={session.evenements} />} />
            <Route
              path="/orga/billetterie"
              element={<Billetterie evenements={session.evenements} organisationSlug={session.organisation.slug} />}
            />
            <Route path="/orga/paiements" element={<Navigate to="/orga/revenus" replace />} />
            <Route
              path="/orga/entreprises"
              element={enConstruction(
                'Entreprises inscrites',
                "Le « parc d'attraction » arrive avec l'enrichissement IA des fiches (CA, effectifs, décideurs, actualités).",
              )}
            />
            <Route
              path="/orga/reseaux"
              element={enConstruction('Réseaux', 'Les réseaux inter-événements arrivent avec les comptes entreprise.')}
            />
            <Route
              path="/orga/sponsors"
              element={enConstruction(
                'Sponsors',
                'Le pipeline sponsors et le générateur de conventions IA arrivent avec la billetterie.',
              )}
            />
            <Route
              path="/orga/revenus"
              element={enConstruction(
                'Revenus & parrainage',
                'Le détail arrive : le suivi des commissions vit déjà dans le tableau de bord, l’onboarding Stripe dans Intégrations.',
              )}
            />
            <Route
              path="/orga/parrainage"
              element={enConstruction(
                'Mes filleuls',
                "L'arbre de parrainage arrive avec les liens d'invitation (le barème 1 %/0,25 % est déjà actif côté paiements).",
              )}
            />
            <Route
              path="/orga/rapports"
              element={enConstruction('Rapports', 'Récap partenaires automatisé et dossier de presse — à venir.')}
            />
            <Route
              path="/orga/equipe"
              element={enConstruction('Mon équipe (Premium)', 'Multi-comptes par organisation — à venir.')}
            />

            {superAdmin ? (
              <>
                <Route path="/orga/plateforme" element={<Plateforme />} />
                <Route path="/orga/organisations" element={<Organisations />} />
                <Route path="/orga/pistes" element={<Pistes />} />
                <Route path="/orga/automatisations" element={<Automatisations />} />
                <Route path="/orga/integrations" element={<Integrations />} />
                <Route path="/orga/commissions" element={<Commissions />} />
                <Route
                  path="/orga/commerciaux"
                  element={enConstruction(
                    'Commerciaux & sous-commerciaux',
                    'La gestion des apporteurs d’affaire arrive avec les liens d’invitation.',
                  )}
                />
                <Route
                  path="/orga/comptes"
                  element={enConstruction(
                    'Entreprises & visiteurs',
                    'La vue plateforme des comptes arrive avec les comptes entreprise.',
                  )}
                />
                <Route
                  path="/orga/ia"
                  element={enConstruction(
                    'Consommation IA',
                    'Le suivi des dépenses DeepSeek/Claude arrive avec les premières fonctionnalités IA branchées.',
                  )}
                />
              </>
            ) : null}

            <Route path="*" element={<Navigate to="/orga" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
